import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import AppSpinner from '../../components/AppSpinner';

interface LogData {
  _id: string;
  timestamp: string;
  level: string;
  message: string;
  meta: any;
}

const AdminSystemLogPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [logs, setLogs] = useState<LogData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalLogs, setTotalLogs] = useState<number>(0);
  const limit = 10;

  const fetchLogs = async (page: number, level: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/system-logs', {
        params: {
          page,
          limit,
          level: level === 'all' ? undefined : level,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setLogs(response.data.logs);
      setTotalPages(response.data.totalPages);
      setTotalLogs(response.data.totalLogs);
      setError(null);
    } catch (err) {
      setError('로그 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchLogs(currentPage, levelFilter);
    }
  }, [isAuthenticated, currentPage, levelFilter]);

  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLevelFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading && logs.length === 0) return <AppSpinner fullscreen tip="Loading..." />;

  return (
    <div className="admin-page">
      <h1>시스템 로그 관리</h1>

      <div
        className="search-filter-container"
        style={{ border: 'none', background: 'transparent', padding: '0', marginBottom: '20px' }}
      >
        <div className="tab-filters" style={{ display: 'flex', gap: '10px' }}>
          {['all', 'info', 'error', 'warn'].map(level => (
            <button
              key={level}
              onClick={() => {
                setLevelFilter(level);
                setCurrentPage(1);
              }}
              className={`btn-tab ${levelFilter === level ? 'active' : ''}`}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: levelFilter === level ? 'none' : '1px solid #ddd',
                backgroundColor: levelFilter === level ? '#3947a7' : '#fff',
                color: levelFilter === level ? '#fff' : '#666',
                cursor: 'pointer',
                fontWeight: levelFilter === level ? 'bold' : 'normal',
                fontSize: '14px',
                transition: 'all 0.2s', 
              }}
            >
              {level === 'all' ? '전체' : level.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="list-info">
        총 로그 <span className="count">{totalLogs}</span> | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>

      <div className="table-scroll">
        <table className="table log-list-table">
          <thead>
            <tr>
              <th style={{ width: '200px' }}>시간</th>
              <th style={{ width: '100px' }}>레벨</th>
              <th>메시지</th>
            </tr>
          </thead>
          <tbody>
            {logs.length > 0 ? (
              logs.map(log => (
                <tr key={log._id}>
                  <td>{new Date(log.timestamp).toLocaleString()}</td>
                  <td>
                    <span className={`status-badge ${log.level === 'info' ? 'normal' : 'suspended'}`}>
                      {log.level.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ textAlign: 'left', whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                    {log.message}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="no-data">
                  로그가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <img src={leftArrow1} alt="처음" />
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <img src={leftArrow2} alt="이전" />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                className={currentPage === pageNum ? 'active' : ''}
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow2} alt="다음" />
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow1} alt="끝" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemLogPage;
